/**
 * Block review screen (PLAN-Z M6): explanations were withheld during the
 * block; they appear here per item, together with the score, per-item timing
 * against the 75 s budget, and retake options (same seed = same 20 items).
 */
import { useMemo } from 'react'
import type { BlockItem, ItemAnswer } from './block.ts'
import { gradeBlockItem } from './block.ts'
import type { Attempt } from '../data/local.ts'
import { bucketStats } from '../stats/compute.ts'
import type { Subtest } from '../generators/types.ts'

export interface BlockResultData {
  subtest: Subtest
  blockSeed: number
  items: BlockItem[]
  answers: ItemAnswer[]
  durations: number[]
  attempts: Attempt[]
  autoSubmitted: boolean
  elapsedMs: number
}

interface ResultsProps {
  data: BlockResultData
  onRetake: () => void
  onNewBlock: () => void
}

function fmtSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`
}

export function Results({ data, onRetake, onNewBlock }: ResultsProps) {
  const graded = useMemo(
    () =>
      data.items.map((item) => ({
        item,
        responses: gradeBlockItem(item, data.answers[item.index] ?? null),
      })),
    [data],
  )
  const responses = graded.flatMap((g) => g.responses)
  const stats = useMemo(() => bucketStats(data.attempts), [data.attempts])
  const score = responses.filter((g) => g.correct).length

  return (
    <div className="exam-results">
      <section className="question-card">
        <h2 className="question-card-title">
          {data.subtest} block · seed {data.blockSeed}
          {data.autoSubmitted ? ' · auto-submitted at 0:00' : ''}
        </h2>
        <div className="drill-summary">
          <p>
            <strong>{score}</strong> / {responses.length} responses correct ·{' '}
            <strong>{Math.round((score / responses.length) * 100)}%</strong>
          </p>
          <p>
            Average {fmtSeconds(stats.avgMs)} per item · typical {fmtSeconds(stats.medianMs)} ·
            slowest 10% {fmtSeconds(stats.p90Ms)}{' '}
            <span className="muted">(budget: 75 s per item)</span>
          </p>
          <p className="muted">
            Total time: {(data.elapsedMs / 1000).toFixed(0)} s of {25 * 60} s. Every attempt was
            saved on this device.
          </p>
        </div>
        <div className="exam-results-actions">
          <button type="button" className="question-submit" onClick={onRetake}>
            Retake this block
          </button>
          <button type="button" className="drill-exit" onClick={onNewBlock}>
            New block
          </button>
        </div>
      </section>

      {graded.map(({ item, responses: itemResponses }) => {
        const explanation = item.question.explanation
        const allCorrect = itemResponses.every((r) => r.correct)
        const anyCorrect = itemResponses.some((r) => r.correct)
        const verdict = allCorrect ? 'correct' : anyCorrect ? 'half right' : 'wrong'
        return (
          <section
            key={item.index}
            className={
              'question-card review-card ' +
              (allCorrect ? 'review-correct' : anyCorrect ? 'review-half' : 'review-wrong')
            }
          >
            <h2 className="question-card-title">
              Item {item.index + 1} · {item.difficulty} · seed {item.seed} — {verdict}
            </h2>
            {itemResponses.map((r, i) => (
              <p key={i} className="review-answers">
                {itemResponses.length > 1 ? (
                  <>
                    Blank {i + 1}:{' '}
                  </>
                ) : null}
                Your answer: <strong>{r.yourAnswer}</strong>
                {!r.correct && (
                  <>
                    {' '}· Right answer: <strong>{r.rightAnswer}</strong>
                  </>
                )}
              </p>
            ))}
            <p className="muted">Time on this item: {fmtSeconds(data.durations[item.index])}</p>
            {explanation.length > 0 ? (
              <div>
                <p className="muted">How it breaks down:</p>
                <ol className="drill-explanation">
                  {explanation.map((step, i) => (
                    <li key={i}>
                      {step.text}
                      {step.lines ? (
                        <span className="step-lines">
                          {step.lines.map((line, j) => (
                            <span key={j}>{line}</span>
                          ))}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </section>
        )
      })}
    </div>
  )
}
